import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { products, deals, categories, brands, testimonials } from '../data/products';
import ProductCard from '../components/ProductCard';
import { useCart } from '../context/CartContext';
import { useCountdown } from '../hooks/useCountdown';
import './index.css';

const MARQUEE = ['iPhones','Samsung Galaxy','MacBook Pro','AirPods Pro','Dell XPS','Apple Watch','iPad Pro','Gaming Laptops','Smart TVs','Cameras','Power Banks','Routers'];

export default function Home({ searchQuery }) {
  const { addToCart } = useCart();
  const { h, m, s } = useCountdown(23 * 3600 + 47 * 60 + 59);

  // Scroll reveal
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const filtered = searchQuery
    ? products.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.brand.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : products;

  const MARQUEE_DOUBLED = [...MARQUEE, ...MARQUEE];

  return (
    <div className="wrapper">
      {/* HERO */}
      <section className="hero">
        <div className="hero-glow-1" /><div className="hero-glow-2" />
        <div className="container">
          <div className="hero-inner">
            <div>
              <div className="hero-badge fade-up">
                <span className="hero-badge-dot" />
                <span className="hero-badge-text">🇿🇼 Zimbabwe's #1 Tech Store — Bindura</span>
              </div>
              <h1 className="hero-h1">
                <span className="fade-up">NEXT-GEN</span>
                <span className="line-cyan fade-up-2">GADGETS</span>
                <span className="line-italic fade-up-3">at your doorstep</span>
              </h1>
              <p className="hero-sub fade-up-3">
                Premium smartphones, laptops, earbuds & tech accessories delivered across Zimbabwe.
                WhatsApp orders welcome — fast, trusted, local.
              </p>
              <div className="hero-actions fade-up-4">
                <Link to="/products" className="btn btn-cyan">🛍️ Shop Now</Link>
                <a href="https://wa.me/263713508064?text=Hi%20Last!%20I%20want%20to%20place%20an%20order" target="_blank" rel="noreferrer" className="btn btn-wa">
                  📱 +263 713 508 064
                </a>
              </div>
              <div className="hero-stats fade-up-4">
                <div><div className="hero-stat-num">500+</div><div className="hero-stat-label">Products</div></div>
                <div><div className="hero-stat-num">2K+</div><div className="hero-stat-label">Customers</div></div>
                <div><div className="hero-stat-num">24/7</div><div className="hero-stat-label">Support</div></div>
              </div>
            </div>
            <div className="hero-visual fade-in">
              <div className="hero-device-showcase">
                <div className="hero-phone">
                  <div className="phone-screen">
                    <div className="phone-status"><span>9:41</span><span>●●●</span></div>
                    <div className="phone-app-icon">⚡</div>
                    <div className="phone-app-name">LASTTECH</div>
                    <div className="phone-product-img">📱</div>
                    <div className="phone-price">$249</div>
                    <div className="phone-btn">ADD TO CART</div>
                  </div>
                </div>
                <div className="hero-laptop">
                  <div className="laptop-screen">
                    <div className="ls-text">💻 LASTTECH.ZW</div>
                    <div className="ls-text">PREMIUM GADGETS</div>
                  </div>
                  <div className="laptop-bar" />
                </div>
                <div className="hero-earbud">🎧</div>
                <div className="hero-tag ht-1">✓ Fast Delivery</div>
                <div className="hero-tag ht-2">🏷️ Best Prices</div>
                <div className="hero-tag ht-3">⭐ 4.9 Rating</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="marquee-wrap">
        <div className="marquee-track">
          {MARQUEE_DOUBLED.map((item, i) => (
            <div key={i} className="m-item">
              <span className={`m-dot ${['cyan','orange','green'][i % 3]}`} />
              {item}
            </div>
          ))}
        </div>
      </div>

      {/* CATEGORIES */}
      <section className="categories-section">
        <div className="container">
          <div className="reveal">
            <p className="sec-label">Browse By Category</p>
            <h2 className="sec-h2">Shop by Category</h2>
            <p className="sec-sub">From flagship phones to pro laptops — everything a tech lover needs.</p>
          </div>
          <div className="cat-grid reveal">
            {categories.map(cat => (
              <Link to={`/products?cat=${cat.id}`} key={cat.id} className="cat-card">
                <span className="cat-icon">{cat.icon}</span>
                <div className="cat-name">{cat.name}</div>
                <div className="cat-count">{cat.count} items</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="products-section">
        <div className="container">
          <div className="products-header reveal">
            <div>
              <p className="sec-label">Our Collection</p>
              <h2 className="sec-h2" style={{marginBottom:0}}>Featured Products</h2>
            </div>
            <Link to="/products" className="btn btn-outline">View All →</Link>
          </div>
          <div className="products-grid">
            {filtered.slice(0, 8).map((p, i) => (
              <ProductCard key={p.id} product={p} delay={i * 0.05} />
            ))}
          </div>
        </div>
      </section>

      {/* FLASH DEAL BANNER */}
      <section className="container">
        <div className="featured-banner reveal">
          <div className="banner-inner">
            <div>
              <div className="banner-tag">🔥 Flash Deal of the Week</div>
              <h2 className="banner-h2">iPhone 15 Pro Max<br /><em>$150 OFF</em> This Week</h2>
              <p className="banner-sub">Titanium design, 48MP camera, A17 Pro chip. Most powerful iPhone ever. Limited stock in Bindura.</p>
              <div className="banner-countdown">
                {[{v:h,l:'Hours'},{v:m,l:'Mins'},{v:s,l:'Secs'}].map(({v,l}) => (
                  <div key={l} className="cd-item">
                    <span className="cd-num">{v}</span>
                    <div className="cd-label">{l}</div>
                  </div>
                ))}
              </div>
              <div style={{display:'flex',gap:'1rem',flexWrap:'wrap'}}>
                <button className="btn btn-cyan" onClick={() => addToCart({id:99,name:'iPhone 15 Pro Max',brand:'Apple',price:999,oldPrice:1149,emoji:'📱'})}>
                  🛒 Add to Cart — $999
                </button>
                <a href="https://wa.me/263713508064?text=Hi%20Last!%20I%20want%20the%20iPhone%2015%20Pro%20Max%20deal!" target="_blank" rel="noreferrer" className="btn btn-outline">
                  📱 Order on WhatsApp
                </a>
              </div>
            </div>
            <div className="banner-visual">📱</div>
          </div>
        </div>
      </section>

      {/* BRANDS */}
      <section className="brands-section">
        <div className="container">
          <p className="sec-label">Trusted Brands We Carry</p>
          <div className="brands-grid reveal">
            {brands.map(b => <div key={b} className="brand-item">{b}</div>)}
          </div>
        </div>
      </section>

      {/* HOT DEALS */}
      <section className="deals-section">
        <div className="container">
          <div className="reveal">
            <p className="sec-label">Limited Time</p>
            <h2 className="sec-h2">Hot Deals</h2>
            <p className="sec-sub">Massive savings on top-tier tech. While stocks last!</p>
          </div>
          <div className="deals-grid reveal">
            {deals.map(d => (
              <div key={d.id} className="deal-card">
                <div className="deal-img">{d.emoji}</div>
                <div className="deal-info">
                  <div className="deal-brand">{d.brand}</div>
                  <div className="deal-name">{d.name}</div>
                  <p className="deal-desc">{d.desc}</p>
                  <div className="deal-price-row">
                    <span className="deal-price">${d.price}</span>
                    <span className="deal-old">${d.oldPrice}</span>
                    <span className="deal-save">SAVE {d.save}</span>
                  </div>
                  <div className="deal-stock">
                    <div className="stock-bar"><div className="stock-fill" style={{width:`${d.stock}%`}} /></div>
                    <span className="stock-text">Only {d.stock}% left in stock</span>
                  </div>
                  <div style={{marginTop:'.75rem',display:'flex',gap:'.5rem',flexWrap:'wrap'}}>
                    <button className="btn btn-cyan btn-sm" onClick={() => addToCart(d)}>🛒 Add to Cart</button>
                    <a href={`https://wa.me/263713508064?text=Hi%20Last!%20I%20want%20the%20${encodeURIComponent(d.name)}`} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm">📱 WhatsApp</a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="why-section">
        <div className="container">
          <div className="reveal" style={{textAlign:'center',marginBottom:'3rem'}}>
            <p className="sec-label" style={{justifyContent:'center'}}>Why LastTech</p>
            <h2 className="sec-h2" style={{textAlign:'center'}}>Zimbabwe's Most Trusted Tech Store</h2>
          </div>
          <div className="why-grid reveal">
            {[
              {icon:'🚀',title:'Same-Day Delivery',desc:'Order before noon in Bindura and get your gadget today. Nationwide delivery available.'},
              {icon:'✅',title:'100% Genuine',desc:'All products are original and come with full manufacturer warranties. No counterfeits.'},
              {icon:'💬',title:'WhatsApp Orders',desc:'Message Last directly on +263 713 508 064. Fast replies, easy ordering, personal service.'},
              {icon:'🔧',title:'After-Sale Support',desc:'Setup help, troubleshooting, and tech advice always available after your purchase.'},
            ].map(w => (
              <div key={w.title} className="why-card">
                <span className="why-icon">{w.icon}</span>
                <div className="why-title">{w.title}</div>
                <p className="why-desc">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="testimonials-section">
        <div className="container">
          <div className="reveal" style={{textAlign:'center',marginBottom:'3rem'}}>
            <p className="sec-label" style={{justifyContent:'center'}}>Customer Reviews</p>
            <h2 className="sec-h2" style={{textAlign:'center'}}>What Zimbabweans Say</h2>
          </div>
          <div className="testi-grid reveal">
            {testimonials.map(t => (
              <div key={t.name} className="testi-card">
                <div className="testi-stars">{'★'.repeat(t.rating)}</div>
                <p className="testi-text">{t.text}</p>
                <div className="testi-author">
                  <div className="testi-avatar">{t.initials}</div>
                  <div>
                    <div className="testi-name">{t.name}</div>
                    <div className="testi-location">📍 {t.location}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="newsletter-section">
        <div className="container">
          <div className="newsletter-inner reveal">
            <p className="sec-label" style={{justifyContent:'center'}}>Stay Updated</p>
            <h2 className="newsletter-h2">Get Exclusive Deals First</h2>
            <p className="newsletter-sub">Join 1,000+ tech lovers across Zimbabwe getting early access to deals and new arrivals.</p>
            <form className="newsletter-form" onSubmit={e => { e.preventDefault(); alert('🎉 Subscribed! Welcome to LastTech deals!'); e.target.reset(); }}>
              <input type="email" className="newsletter-input" placeholder="your@email.com" required />
              <button type="submit" className="btn btn-cyan">Subscribe 🚀</button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
